import type { IFieldDefinition, IFormDefinition, IFormGroup, IRecordTypeDefinition } from '@platform/core/types';

/** Порядок стандартних груп (прототип v4, stdGroups): «Подробиці» першими, далі Управління · Зв'язки · Планування · Дати · Нотатки. */
const STANDARD_ORDER = ['Details', 'Management', 'Links', 'Planning', 'Dates', 'Notes'];
const MANAGEMENT = ['priority', 'severity', 'rank'];

/**
 * Групи повної форми (UIUX forms/groups, FORM_LAYOUT §1): визначення форми — точно як задано: порядок груп, їхні поля в заданому
 * порядку (лише ті, що є в типі запису), прапорець `full` і розкладка; порожні групи приховано. Полів, яких визначення не розміщує,
 * рушій не додає. Лише тип без жодної групи у визначенні отримує стандартні групи за типами полів (standardGroups). Чиста функція.
 */
export function resolveGroups(rt: IRecordTypeDefinition): IFormGroup[] {
  const known = new Set(rt.fields.map((f) => f.key));
  if (!rt.form.groups.length) return standardGroups(rt.fields, rt.form);
  return rt.form.groups
    .map((g) => ({ ...g, fields: g.fields.filter((k) => known.has(k)) }))
    .filter((g) => g.fields.length > 0);
}

/**
 * Стандартні групи для типу без груп у визначенні (прототип v4, stdGroups): Управління (виконавець, інші люди, пріоритет, важливість,
 * відносний пріоритет) · Зв'язки (посилання) · Планування (напрям, ітерація) · Дати · Подробиці (решта) · Нотатки (довгі тексти).
 * Назва, поля під назвою і статус ключового рядка вважаються розміщеними, тож у групи не потрапляють. Чиста функція.
 */
export function standardGroups(fields: IFieldDefinition[], form?: Pick<IFormDefinition, 'keyLine' | 'underTitle'>): IFormGroup[] {
  const placed = new Set(['title', ...(form?.underTitle ?? []), ...(form?.keyLine ?? []).filter((k) => k === 'status')]);
  const free = fields.filter((f) => !placed.has(f.key));
  const keysOf = (pick: (f: IFieldDefinition) => boolean) => free.filter(pick).map((f) => f.key);
  const groups: Array<[string, string[]]> = [
    ['Management', [...keysOf((f) => f.key === 'assignee'), ...keysOf((f) => f.type === 'user' && f.key !== 'assignee'), ...MANAGEMENT.filter((k) => free.some((f) => f.key === k))]],
    ['Links', keysOf((f) => f.type === 'ref' || f.type === 'lookup')],
    ['Planning', keysOf((f) => f.type === 'area' || f.type === 'iteration')],
    ['Dates', keysOf((f) => f.type === 'date')],
    ['Details', keysOf((f) => !MANAGEMENT.includes(f.key) && !['user', 'date', 'textarea', 'area', 'iteration', 'ref', 'lookup'].includes(f.type))],
    ['Notes', keysOf((f) => f.type === 'textarea')],
  ];
  return groups
    .filter(([, keys]) => keys.length > 0)
    .sort(([a], [b]) => STANDARD_ORDER.indexOf(a) - STANDARD_ORDER.indexOf(b))
    .map(([title, keys]) => ({ id: title.toLowerCase(), title: { en: title }, full: title === 'Notes', fields: keys }));
}

/** Поля ключового рядка визначення, що існують у типі запису (швидке створення бере їх як обов'язкові до заповнення). */
export function keyLineFields(rt: IRecordTypeDefinition): IFieldDefinition[] {
  return rt.form.keyLine.map((k) => rt.fields.find((f) => f.key === k)).filter((f): f is IFieldDefinition => Boolean(f));
}
